/**
 * MembersOnlyGate — candado de lo que es solo para miembros (freemium).
 *
 * El Free ve la pantalla (sabe que existe y qué trae) pero no su contenido:
 * una vista previa neutra de fondo y una tarjeta con el upgrade adentro del
 * panel (Mi cuenta → Suscripción). A un miembro, guru o admin le deja pasar
 * directo. Mientras carga, spinner: nunca un candado por parpadeo.
 *
 * La vista previa es decorativa (bloques grises, sin datos reales) y sin
 * `filter: blur`: los blur grandes rompen el render en Safari iOS.
 */
import { Link } from 'react-router-dom';
import { Check, GraduationCap, Lock, MessageSquare, Trophy, UserPlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useMembership, UPGRADE_PATH } from '@/lib/hooks/useMembership';
import { LAUNCH_PROMO, isLaunchPromoActive, promoPriceLabel } from '@/lib/promo';

export type MembersFeature = 'community' | 'achievements' | 'referrals';

const COPY: Record<MembersFeature, {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  text: string;
  perks: string[];
}> = {
  community: {
    icon: MessageSquare,
    title: 'La comunidad es para miembros',
    text: 'Aquí conversan los miembros con los gurus: preguntas, casos reales y respuestas de quienes ya lo hicieron.',
    perks: [
      'Publica, comenta y reacciona con gurus y miembros',
      'Menciona a otros y recibe respuestas',
      'Suma puntos por participar',
    ],
  },
  achievements: {
    icon: Trophy,
    title: 'Puntos y logros son para miembros',
    text: 'Los miembros suman puntos al aprender y participar, y los canjean por cupones para sesiones 1:1 con gurus.',
    perks: [
      'Puntos por lecciones, quizzes, tareas y comunidad',
      'Rachas y logros',
      'Cupones canjeables en mentorías',
    ],
  },
  referrals: {
    icon: UserPlus,
    title: 'Referidos es para miembros',
    text: 'Invita a otros al club y gana puntos cuando se hacen miembros.',
    perks: [
      'Tu link personal de invitación',
      'Puntos por cada referido que se hace miembro',
      'Cupones canjeables en mentorías',
    ],
  },
};

export function MembersOnlyGate({
  feature,
  children,
}: {
  feature: MembersFeature;
  children: React.ReactNode;
}) {
  const { loading, isFreeClient } = useMembership();

  if (loading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-accent border-t-transparent" />
      </div>
    );
  }
  if (!isFreeClient) return <>{children}</>;

  const c = COPY[feature];
  return (
    <div className="relative mx-auto max-w-3xl">
      <FakePreview />
      <div className="absolute inset-x-0 top-6 flex justify-center px-2">
        <LockedCard icon={c.icon} title={c.title} text={c.text} perks={c.perks} />
      </div>
    </div>
  );
}

/** Tarjeta de candado reutilizable (también la usan eventos y cursos). */
export function LockedCard({
  icon: Icon = Lock,
  title,
  text,
  perks,
  secondary,
}: {
  icon?: React.ComponentType<{ className?: string }>;
  title: string;
  text: string;
  perks?: string[];
  /** Acción secundaria opcional (por defecto: ver los cursos gratis). */
  secondary?: { label: string; to: string } | null;
}) {
  const second = secondary === undefined
    ? { label: 'Ver mis cursos gratis', to: '/dashboard/courses?plan=free' }
    : secondary;
  return (
    <div className="w-full max-w-md rounded-2xl border border-border bg-white p-7 text-center shadow-elevated">
      <span className="relative mx-auto inline-flex h-12 w-12 items-center justify-center rounded-full bg-brand-accent/10 text-brand-accent">
        <Icon className="h-6 w-6" />
        <span className="absolute -bottom-1 -right-1 inline-flex h-5 w-5 items-center justify-center rounded-full bg-brand-primary text-white">
          <Lock className="h-3 w-3" />
        </span>
      </span>
      <h2 className="font-display mt-4 text-xl font-semibold text-brand-text-strong">{title}</h2>
      <p className="mt-2 text-sm text-muted-foreground">{text}</p>
      {perks && perks.length > 0 && (
        <ul className="mt-4 space-y-1.5 text-left text-sm">
          {perks.map((p) => (
            <li key={p} className="flex items-start gap-2">
              <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-brand-accent" />
              <span>{p}</span>
            </li>
          ))}
        </ul>
      )}
      <Button asChild variant="cta" size="lg" className="mt-6 w-full">
        <Link to={UPGRADE_PATH}>Conocer En Vivo</Link>
      </Button>
      {isLaunchPromoActive() && (
        <p className="mt-2 text-xs font-medium text-brand-primary">
          Lanzamiento: En Vivo a {promoPriceLabel('pro')}/mes para siempre si entras antes del {LAUNCH_PROMO.endsLabel}
        </p>
      )}
      {second && (
        <Button asChild variant="ghost" className="mt-2 w-full">
          <Link to={second.to}>
            <GraduationCap className="h-4 w-4" /> {second.label}
          </Link>
        </Button>
      )}
    </div>
  );
}

function FakePreview() {
  return (
    <div aria-hidden className="pointer-events-none select-none">
      <div className="space-y-3 opacity-50">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="rounded-2xl border border-border bg-white p-5">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-full bg-[#E6EEF1]" />
              <div className="space-y-1.5">
                <div className="h-2.5 w-36 rounded bg-[#E6EEF1]" />
                <div className="h-2 w-20 rounded bg-[#EEF3F5]" />
              </div>
            </div>
            <div className="mt-4 space-y-2">
              <div className="h-2.5 w-full rounded bg-[#EEF3F5]" />
              <div className="h-2.5 w-11/12 rounded bg-[#EEF3F5]" />
              <div className="h-2.5 w-2/3 rounded bg-[#EEF3F5]" />
            </div>
          </div>
        ))}
      </div>
      <div className="absolute inset-0 bg-gradient-to-b from-white/30 via-white/60 to-white" />
    </div>
  );
}
